import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { HxStoreService, HxUiLabelPipe, PagedList, StoreFullModel, uiLabel } from 'hx-services';
import { HxCitySelectComponent, HxOrgSelectComponent, HxPagedListDirective } from 'hx-components';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { NzPageHeaderComponent, NzPageHeaderExtraDirective, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzButtonComponent, NzButtonGroupComponent } from 'ng-zorro-antd/button';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzCollapseComponent, NzCollapsePanelComponent } from 'ng-zorro-antd/collapse';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { FormsModule } from '@angular/forms';
import { NzTableCellDirective, NzTableComponent, NzTbodyComponent, NzThAddOnComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NgFor, NgIf } from '@angular/common';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';

interface ListFilter {
  name: string;
  options: { label: string, value: string | number, checked?: boolean }[];
  isVisible: boolean;
  isActive: boolean;
  operator: 'and' | 'or';
}

interface StoreFullItemModel extends StoreFullModel {
  isLoading?: boolean;
}

@Component({
    selector: 'hc-store-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, NzPageHeaderExtraDirective, NzButtonComponent, RouterLink, NzIconDirective, NzCollapseComponent, NzCollapsePanelComponent, NzRowDirective, NzColDirective, HxCitySelectComponent, FormsModule, HxOrgSelectComponent, NzTableComponent, NzTheadComponent, NzTrDirective, NzTableCellDirective, NzThMeasureDirective, NzThAddOnComponent, NzTbodyComponent, NgFor, NgIf, NzTooltipDirective, NzButtonGroupComponent, NzWaveDirective, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class StoreListComponent extends HxPagedListDirective<StoreFullItemModel> implements OnInit {
  filter: {
    orgId?: number;
    cityId?: number
  } = {};
  isLoading = {
    cfCode: false
  };

  constructor(
    override router: Router,
    override aRoute: ActivatedRoute,
    private storeService: HxStoreService,
    private notification: NzNotificationService,
    private tr: TranslocoService,
  ) {
    super(router, aRoute);
    this.limit = 20;
  }

  ngOnInit() {
    this.aRoute.queryParamMap.pipe(takeUntil(this.$destroyed))
      .subscribe(paramMap => super.load(paramMap));
  }

  resetFilter(filter: ListFilter) {
    filter.options.forEach(op => op.checked = false);
  }

  applyFilters() {
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: {
        cityId: this.filter.cityId,
        orgId: this.filter.orgId,
      },
      queryParamsHandling: 'merge',
    });
  }

  applyFilter(filter: ListFilter) {
    const checkedArr = filter.options.filter(v => v.checked).map(v => v.value);
    let filterParam: string | null = null;
    let filterOp: 'and' | 'or' | null = null;
    if (checkedArr.length !== 0) {
      filterParam = checkedArr.join(',');
      filterOp = filter.operator;
    }
    const queryParams: Params = {};
    queryParams[filter.name] = filterParam;
    queryParams[filter.name + '_op'] = filterOp;
    filter.isVisible = false;
    this.router.navigate([], {
      relativeTo: this.aRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  async getCfCode(store: StoreFullItemModel) {
    store.isLoading = true;
    try {
      const result = await this.storeService.getStoreCfCode(store.id);
      this.notification.success(this.tr.translate('code') + ' ' + uiLabel(this.tr.getActiveLang(), store.title), result.code);
    } catch (err: any) {
      this.notification.error(this.tr.translate('code'), this.tr.translate('nt.error'));
    } finally {
      store.isLoading = false;
    }
  }

  protected loadList(): Promise<PagedList<StoreFullItemModel>> {
    return this.storeService.getFullStores({
      page: this.page,
      includeDisabled: true,
      limit: this.limit,
      cityId: this.filter.cityId,
      orgId: this.filter.orgId,
      sort: this.getSortParams(),
    });
  }

  remove(id: number): void {
    this.storeService.removeStore(id).subscribe(() => this.loadData());
  }
}
